/**
 * Reproductor de música y ambiente con archivos reales.
 *
 * Dos pistas de música se cruzan con un fundido para que los cambios de
 * escenario o de combate no corten en seco. Si un archivo no puede cargarse
 * (despliegue incompleto, bloqueo del navegador) se delega en el generador
 * procedural para que la partida nunca quede en silencio.
 */

import { ProceduralAudio, type AmbientProfile } from '../render/procedural-audio';
import {
  AUDIO_BASE_URL,
  AMBIENCE_LIBRARY,
  MUSIC_LIBRARY,
  type AmbienceName,
  type MusicMood
} from './library';

const FADE_MS = 900;
const MUSIC_VOLUME = 0.35;
const AMBIENCE_VOLUME = 0.25;

/** Perfiles de ambiente procedural que tienen una grabación real equivalente. */
const AMBIENCE_BY_PROFILE: Partial<Record<AmbientProfile, AmbienceName>> = {
  night: 'bosque-noche',
  fireflies: 'bosque-noche',
  embers: 'fogata'
};

interface Deck {
  el: HTMLAudioElement;
  mood: MusicMood | null;
}

export class MusicDirector {
  private readonly decks: Deck[] = [];
  private active: Deck | null = null;
  private ambienceEl: HTMLAudioElement | null = null;
  private profile: AmbientProfile | null = null;

  private enabled = false;
  private reducedMotion = false;
  private mood: MusicMood | null = null;
  private filesBroken = false;
  private fadeTimer: number | null = null;
  private gestureHandler: (() => void) | null = null;
  private destroyed = false;

  constructor(
    private readonly procedural: ProceduralAudio,
    private readonly baseUrl: string = AUDIO_BASE_URL
  ) {}

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
    this.apply();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.apply();
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** Cambia la pista de fondo; el fundido evita cortes bruscos. */
  setMood(mood: MusicMood): void {
    if (this.mood === mood) return;
    this.mood = mood;
    this.apply();
  }

  /** Fija el ambiente por perfil; usa la grabación real cuando existe. */
  setAmbientProfile(profile: AmbientProfile): void {
    if (this.profile === profile) return;
    this.profile = profile;
    this.applyAmbience();
  }

  stopAmbient(): void {
    this.profile = null;
    this.applyAmbience();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearFade();
    this.removeGestureHandler();
    for (const deck of this.decks) {
      deck.el.pause();
      deck.el.removeAttribute('src');
    }
    this.decks.length = 0;
    this.stopAmbienceFile();
    this.active = null;
  }

  /* ------------------------------ interno ------------------------------ */

  private get filesUsable(): boolean {
    return !this.filesBroken && !this.reducedMotion;
  }

  /** Aplica el estado deseado: qué suena ahora mismo. */
  private apply(): void {
    const wantsMusic = this.enabled && !this.reducedMotion && this.mood !== null;

    if (!this.filesUsable) {
      this.fadeOutActive();
      this.procedural.setMusicEnabled(wantsMusic);
      return;
    }
    this.procedural.setMusicEnabled(false);
    if (!wantsMusic) {
      this.fadeOutActive();
      return;
    }
    this.startMood(this.mood as MusicMood);
  }

  /** Elige entre la grabación real del ambiente y el generador procedural. */
  private applyAmbience(): void {
    const name: AmbienceName | null =
      this.profile && this.filesUsable ? AMBIENCE_BY_PROFILE[this.profile] ?? null : null;

    if (name) {
      this.procedural.stopAmbient();
      this.playAmbienceFile(name);
      return;
    }
    this.stopAmbienceFile();
    if (this.profile && !this.reducedMotion) this.procedural.playAmbient(this.profile);
    else this.procedural.stopAmbient();
  }

  private startMood(mood: MusicMood): void {
    if (this.active?.mood === mood && !this.active.el.paused) return;

    const previous = this.active;
    const deck = this.freeDeck(previous);
    deck.mood = mood;
    deck.el.loop = true;
    deck.el.volume = 0;
    deck.el.src = this.baseUrl + MUSIC_LIBRARY[mood].src;
    deck.el.load();

    const started = deck.el.play();
    if (started) started.catch(() => this.waitForGesture());

    this.active = deck;
    this.crossfade(previous, deck, MUSIC_VOLUME);
  }

  /** Reutiliza el mazo libre o, si ambos suenan, el que no está activo. */
  private freeDeck(keep: Deck | null): Deck {
    const idle = this.decks.find((d) => d !== keep);
    if (idle) return idle;
    const deck: Deck = { el: new Audio(), mood: null };
    deck.el.addEventListener('error', () => this.onFileError());
    this.decks.push(deck);
    return deck;
  }

  private onFileError(): void {
    if (this.filesBroken || this.destroyed) return;
    this.filesBroken = true;
    this.fadeOutActive();
    this.stopAmbienceFile();
    this.procedural.setMusicEnabled(this.enabled && !this.reducedMotion && this.mood !== null);
    this.applyAmbience();
  }

  private crossfade(from: Deck | null, to: Deck, target: number): void {
    this.clearFade();
    const fromStart = from?.el.volume ?? 0;
    const started = performance.now();
    this.fadeTimer = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - started) / FADE_MS);
      to.el.volume = target * k;
      if (from) from.el.volume = fromStart * (1 - k);
      if (k >= 1) {
        this.clearFade();
        if (from && from !== to) {
          from.el.pause();
          from.mood = null;
        }
      }
    }, 50);
  }

  private fadeOutActive(): void {
    const deck = this.active;
    this.active = null;
    if (!deck) return;
    this.clearFade();
    const start = deck.el.volume;
    const began = performance.now();
    this.fadeTimer = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - began) / FADE_MS);
      deck.el.volume = start * (1 - k);
      if (k >= 1) {
        this.clearFade();
        deck.el.pause();
        deck.mood = null;
      }
    }, 50);
  }

  private playAmbienceFile(name: AmbienceName): void {
    if (!this.filesUsable) return;
    if (!this.ambienceEl) {
      this.ambienceEl = new Audio();
      this.ambienceEl.loop = true;
      this.ambienceEl.volume = AMBIENCE_VOLUME;
      this.ambienceEl.addEventListener('error', () => this.onFileError());
    }
    this.ambienceEl.src = this.baseUrl + AMBIENCE_LIBRARY[name].src;
    this.ambienceEl.load();
    const started = this.ambienceEl.play();
    if (started) started.catch(() => this.waitForGesture());
  }

  private stopAmbienceFile(): void {
    if (!this.ambienceEl) return;
    this.ambienceEl.pause();
    this.ambienceEl.removeAttribute('src');
  }

  /** El navegador exige un gesto: se reintenta la reproducción en el siguiente. */
  private waitForGesture(): void {
    if (this.gestureHandler || this.destroyed) return;
    this.gestureHandler = () => {
      this.removeGestureHandler();
      this.apply();
      this.applyAmbience();
    };
    document.addEventListener('pointerdown', this.gestureHandler, { once: true });
    document.addEventListener('keydown', this.gestureHandler, { once: true });
  }

  private removeGestureHandler(): void {
    if (!this.gestureHandler) return;
    document.removeEventListener('pointerdown', this.gestureHandler);
    document.removeEventListener('keydown', this.gestureHandler);
    this.gestureHandler = null;
  }

  private clearFade(): void {
    if (this.fadeTimer !== null) {
      clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
  }
}
